import { z } from 'zod';

const ignoreEmpty = (arg: unknown) => (arg === '' ? undefined : arg);

export const EnvSchema = z
  .object({
    baseUrl: z.preprocess(ignoreEmpty, z.string().optional().default('https://api.cognitedata.com')),
    strictMode: z.boolean().optional().default(false),
    appName: z.preprocess(ignoreEmpty, z.string().optional().default('Cognite Dashboard')),
    dataInstanceSpace: z.preprocess(ignoreEmpty, z.string().optional()),
    basePath: z.preprocess(ignoreEmpty, z.string().optional().default('/')),
    displayTz: z.preprocess(ignoreEmpty, z.string().optional().default('Asia/Dubai')),
    auth: z.preprocess(
      (arg: object) => (Object.values(arg).filter(Boolean).length === 0 ? undefined : arg),
      z
        .object({
          clientId: z.preprocess(ignoreEmpty, z.string()),
          tenantId: z.preprocess(ignoreEmpty, z.string()),
          cluster: z.preprocess(ignoreEmpty, z.string()),
          project: z.preprocess(ignoreEmpty, z.string()),
          redirectUri: z.preprocess(
            ignoreEmpty,
            z
              .string()
              .optional()
              .default(() => document.location.origin),
          ),
        })
        .strict()
        .optional(),
    ),
    cdf: z.preprocess(
      (arg: object) => (Object.values(arg).filter(Boolean).length === 0 ? undefined : arg),
      z
        .object({
          url: z.preprocess(ignoreEmpty, z.string().optional()),
        })
        .strict()
        .optional(),
    ),
  })
  .strict();

export type EnvSchema = z.infer<typeof EnvSchema>;

/**
 * Single source of truth mapping each nested {@link EnvSchema} leaf path to the
 * raw `VITE_*` key it is injected from. `env.ts` reduces over this manifest to
 * build the `EnvSchema.parse` input, and `env.d.ts` derives the known ambient
 * `import.meta.env` keys from its value union. Adding an env-sourced field is a
 * single edit here plus the matching `EnvSchema` field.
 *
 * Keys are dotted leaf paths into the parsed shape; values are the raw Vite keys.
 */
export const ENV_KEYS = {
  baseUrl: 'VITE_BASE_URL',
  strictMode: 'VITE_IS_STRICT_MODE',
  appName: 'VITE_APP_NAME',
  basePath: 'VITE_APP_BASE_PATH',
  displayTz: 'VITE_APP_DISPLAY_TZ',
  'auth.clientId': 'VITE_APP_CLIENT_ID',
  'auth.tenantId': 'VITE_APP_TENANT_ID',
  'auth.cluster': 'VITE_APP_CLUSTER',
  'auth.project': 'VITE_APP_PROJECT',
  'auth.redirectUri': 'VITE_APP_REDIRECT_URI',
  'cdf.url': 'VITE_APP_CDF_URL',
} as const satisfies Record<string, `VITE_${string}`>;

/** Raw `VITE_*` keys known to {@link ENV_KEYS}. */
export type EnvViteKey = (typeof ENV_KEYS)[keyof typeof ENV_KEYS];
